// Helpers for diary photos backed by Supabase Storage (`diary-photos` bucket).
// Photos are stored at: <user_id>/<entry_id>/<photo_id>.<ext>
// Records in the diary entry's `photos` array look like:
//   { id, path, url, name }
// The persisted `url` is just the signed URL from upload time (expires after
// 24h); only `path` is durable — the UI resolves fresh signed URLs from it.

import { supabase } from './supabase';

const BUCKET = 'diary-photos';
const SIGNED_URL_TTL_SECONDS = 60 * 60 * 24; // 24h; refreshed each session

const extOf = (filename) => {
  const m = /\.([a-zA-Z0-9]+)$/.exec(filename || '');
  return (m && m[1]) || 'jpg';
};

const newId = () => `${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;

// Uploads each file independently so one failure doesn't discard the records
// of files already uploaded in the same batch.
// Returns { photos: successfulRecords, failedCount }.
export async function uploadPhotos(userId, entryId, files) {
  const photos = [];
  let failedCount = 0;
  for (const f of files) {
    const id = newId();
    const path = `${userId}/${entryId}/${id}.${extOf(f.name)}`;
    try {
      const { error } = await supabase.storage.from(BUCKET).upload(path, f, {
        contentType: f.type || 'image/jpeg',
        upsert: false
      });
      if (error) throw error;
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn('[diaryPhotos] upload failed for', f.name, err);
      failedCount += 1;
      continue;
    }
    // Signing is best-effort: the component resolves fresh URLs from `path` anyway.
    let url = null;
    try {
      const { data: signed } = await supabase.storage
        .from(BUCKET)
        .createSignedUrl(path, SIGNED_URL_TTL_SECONDS);
      url = (signed && signed.signedUrl) || null;
    } catch {
      // ignore — the file is uploaded, so keep the record
    }
    photos.push({ id, path, url, name: f.name });
  }
  return { photos, failedCount };
}

export async function deletePhoto(photo) {
  if (!photo || !photo.path) return;
  const { error } = await supabase.storage.from(BUCKET).remove([photo.path]);
  if (error) {
    // eslint-disable-next-line no-console
    console.warn('[diaryPhotos] delete failed', error);
  }
}

// Best-effort bulk delete (e.g. when a whole entry is removed). Never throws.
export async function deletePhotos(paths) {
  const valid = (paths || []).filter(Boolean);
  if (valid.length === 0) return;
  try {
    const { error } = await supabase.storage.from(BUCKET).remove(valid);
    if (error) throw error;
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[diaryPhotos] bulk delete failed', err);
  }
}

// Create fresh signed URLs for the given storage paths.
// Returns a { path: signedUrl } map; paths that fail to sign are omitted.
export async function refreshSignedUrls(paths) {
  const map = {};
  const valid = (paths || []).filter(Boolean);
  if (valid.length === 0) return map;
  try {
    const { data, error } = await supabase.storage
      .from(BUCKET)
      .createSignedUrls(valid, SIGNED_URL_TTL_SECONDS);
    if (error) throw error;
    (data || []).forEach((d, i) => {
      if (d && !d.error && d.signedUrl) map[valid[i]] = d.signedUrl;
    });
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[diaryPhotos] signed URL refresh failed', err);
  }
  return map;
}
